// Match Lab event map: the game's detected goals, shots and set pieces
// on a 105 x 68 pitch, beside the spatial view.
//
// L4 positions are in the ACTING team's attacking frame (x 0-105 toward
// the goal it attacks, y 0-68; l4/geometry.rotate_to_attacking). They are
// drawn the way the VIDEO runs: for one half, each team attacks the end it
// attacks on screen in that half (match-lab-info video_directions, from
// L3's camera view); for the whole game, each team keeps its first-half
// direction for all its events.
import { useState } from 'react'

/** Seconds of build-up a goal plays before its own frame when clicked: the
 *  goal's frame is the ball crossing the line, and the move that made it is
 *  what a coach wants to see (Ben, 2026-10-24). Other events seek exactly. */
export const GOAL_LEAD_S = 7

/** The frame a click on an event seeks to: a goal GOAL_LEAD_S early, never
 *  before frame 0; any other event its own frame. Timeline and pitch both
 *  route through this. fps is the game's recorded rate (Match Lab never
 *  shows a game without one). */
export function eventSeekFrame(kind: string, frame: number, fps: number): number {
  if (kind !== 'goal') return frame
  return Math.max(0, frame - Math.round(GOAL_LEAD_S * fps))
}

export type PitchEvent = {
  key: string
  kind: 'goal' | 'shot' | 'set_piece'
  frame_idx: number
  team_id: number | null
  x_att_m: number | null
  y_att_m: number | null
  label: string
  outcome?: string | null
  /** The event's row id (goal_id / action_id), for reviews. */
  event_id?: number
  /** The standing review, when there is one (MatchLab EventReview). */
  review?: { action: string } | null
  /** The team whose attacking frame x/y_att_m are in, when it is not
   *  team_id: a goal is placed at its shot, and an own goal's shooter is
   *  the other team. */
  frame_team_id?: number | null
  /** goal_type / shot outcome / restart_type, as reviewed. */
  detail?: string | null
}

const L = 105
const W = 68

/** SVG position of an attacking-frame point for a team attacking right
 *  (as stored) or left (turned 180 degrees). SVG y runs down, pitch y up. */
export function toPitch(x: number, y: number, attacksRight: boolean) {
  return attacksRight ? { x, y: W - y } : { x: L - x, y }
}

export type Half = 'all' | 1 | 2
export type VideoDirections = Record<string, { home_attacks_right: boolean }>

/** Does home attack right on the video for this selection? The whole
 *  game uses the first half's direction. null when L3 could not say. */
export function homeAttacksRight(dirs: VideoDirections | undefined,
                                 half: Half): boolean | null {
  const d = dirs?.[String(half === 'all' ? 1 : half)]
  return d ? d.home_attacks_right : null
}

export function MatchLabPitch({ events, homeTeamId, homeColor, awayColor,
                                homeName, awayName, currentFrame, fps,
                                onSeek, periods = [], directions }: {
  events: PitchEvent[]
  /** Frame bounds per half, to pick a half's events. */
  periods?: { period_id: number; start_frame_idx: number; end_frame_idx: number }[]
  /** match-lab-info video_directions. */
  directions?: VideoDirections
  homeTeamId: number | null
  homeColor: string
  awayColor: string
  homeName: string
  awayName: string
  currentFrame: number
  fps: number
  onSeek: (frame: number) => void
}) {
  const [show, setShow] = useState({ goal: true, shot: true,
                                     set_piece: false })
  const [half, setHalf] = useState<Half>('all')
  const known = homeAttacksRight(directions, half)
  const homeRight = known ?? true
  const inHalf = (e: PitchEvent) => {
    if (half === 'all') return true
    const p = periods.find((q) => q.period_id === half)
    return !!p && e.frame_idx >= p.start_frame_idx && e.frame_idx <= p.end_frame_idx
  }
  const scoped = events.filter(inHalf)
  // An event within 5 s either side of the playhead is picked out.
  const near = 5 * fps
  const placed = scoped.filter((e) => show[e.kind]
    && e.x_att_m != null && e.y_att_m != null)
  const counts = (k: PitchEvent['kind']) =>
    scoped.filter((e) => e.kind === k).length
  const halves: [Half, string][] = [['all', 'All'], [1, '1st half'], [2, '2nd half']]

  return (
    <div style={{ borderRadius: '4px', background: '#1a1a1e',
                  overflow: 'hidden' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between',
                    alignItems: 'center', padding: '3px 10px', gap: '8px',
                    borderBottom: '1px solid rgba(255,255,255,0.1)',
                    fontSize: '11px', color: '#888' }}>
        <span style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
          Event Map
          <span role="group" aria-label="Which half" style={{ display: 'flex' }}>
            {halves.map(([h, label]) => (
              <button key={String(h)} aria-pressed={half === h}
                      onClick={() => setHalf(h)}
                      style={{ fontSize: '10px', padding: '0 5px',
                               cursor: 'pointer', border: '1px solid #444',
                               marginLeft: '-1px',
                               background: half === h ? '#444' : 'none',
                               color: half === h ? '#fff' : '#999' }}>
                {label}
              </button>
            ))}
          </span>
        </span>
        <span style={{ display: 'flex', gap: '8px' }}>
          {(['goal', 'shot', 'set_piece'] as const).map((k) => (
            <label key={k} style={{ display: 'inline-flex', gap: '3px',
                                    alignItems: 'center', cursor: 'pointer' }}>
              <input type="checkbox" checked={show[k]}
                     onChange={(e) => setShow({ ...show, [k]: e.target.checked })} />
              {k === 'goal' ? 'Goals' : k === 'shot' ? 'Shots' : 'Set pieces'}
              {' '}({counts(k)})
            </label>
          ))}
        </span>
      </div>
      <svg viewBox={`-2 -2 ${L + 4} ${W + 4}`} role="img"
           aria-label="Event map" style={{ display: 'block', width: '100%' }}>
        <PitchLines />
        {placed.map((e) => {
          const isHome = e.team_id === homeTeamId
          const frameHome = (e.frame_team_id ?? e.team_id) === homeTeamId
          const p = toPitch(e.x_att_m!, e.y_att_m!,
                            frameHome ? homeRight : !homeRight)
          const color = isHome ? homeColor : awayColor
          const hot = Math.abs(e.frame_idx - currentFrame) <= near
          const title = `${e.label} — ${isHome ? homeName : awayName}, f${e.frame_idx}`
          const common = {
            onClick: () => onSeek(eventSeekFrame(e.kind, e.frame_idx, fps)),
            style: { cursor: 'pointer' },
            'data-kind': e.kind,
          }
          if (e.kind === 'set_piece') {
            return (
              <rect key={e.key} {...common} x={p.x - 0.9} y={p.y - 0.9}
                    width={1.8} height={1.8}
                    transform={`rotate(45 ${p.x} ${p.y})`}
                    fill={color} fillOpacity={0.6}
                    stroke={hot ? '#fff' : 'none'} strokeWidth={0.4}>
                <title>{title}</title>
              </rect>
            )
          }
          const goal = e.kind === 'goal'
          const offTarget = e.outcome === 'off_target'
          return (
            <circle key={e.key} {...common} cx={p.x} cy={p.y}
                    r={goal ? 1.8 : 1.2}
                    fill={offTarget ? 'none' : color}
                    fillOpacity={e.outcome === 'blocked' ? 0.45 : 1}
                    stroke={goal || hot ? '#fff' : color}
                    strokeWidth={goal ? 0.5 : hot ? 0.5 : 0.35}>
              <title>{title}</title>
            </circle>
          )
        })}
      </svg>
      <div style={{ display: 'flex', justifyContent: 'space-between',
                    padding: '2px 10px 4px', fontSize: '10px', color: '#888' }}>
        {/* Who attacks which way, as the video shows it. */}
        <span data-testid="attacks-left">
          <Dot color={homeRight ? awayColor : homeColor} /> {'\u2190'}{' '}
          {homeRight ? awayName : homeName}
        </span>
        <span style={{ opacity: 0.7 }}
              title={known == null ? 'L3 has no camera view for this game: '
                + 'direction assumed, not taken from the video' : undefined}>
          ● scored/on · ◐ blocked · ○ off target · ◆ set piece
          {known == null ? ' · direction unverified' : ''}
        </span>
        <span data-testid="attacks-right">
          {homeRight ? homeName : awayName} {'\u2192'}{' '}
          <Dot color={homeRight ? homeColor : awayColor} />
        </span>
      </div>
    </div>
  )
}

function Dot({ color }: { color: string }) {
  return <span style={{ display: 'inline-block', width: 7, height: 7,
                        borderRadius: '50%', background: color }} />
}

function PitchLines() {
  const s = { fill: 'none', stroke: 'rgba(255,255,255,0.35)',
              strokeWidth: 0.25 }
  const box = (x: number, w: number, h: number) =>
    <rect x={x} y={(W - h) / 2} width={w} height={h} {...s} />
  return (
    <g>
      <rect x={0} y={0} width={L} height={W} {...s} fill="#20352a" />
      <line x1={L / 2} y1={0} x2={L / 2} y2={W} {...s} />
      <circle cx={L / 2} cy={W / 2} r={9.15} {...s} />
      {box(0, 16.5, 40.32)}{box(L - 16.5, 16.5, 40.32)}
      {box(0, 5.5, 18.32)}{box(L - 5.5, 5.5, 18.32)}
      {box(-1.5, 1.5, 7.32)}{box(L, 1.5, 7.32)}
    </g>
  )
}
